import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  DEFAULT_SCOPE,
  ProductLoadingService,
  ProductScope,
  ProductService,
  StateWithProduct
} from '@spartacus/core'
import { saveAs } from 'file-saver'
import { BehaviorSubject, Observable, of } from 'rxjs'
import { first, tap } from 'rxjs/operators'
import { EBStructuredProductData } from '../../../cms/interfaces/eb-page.interface'
import { EbProduct, EbProductSearchPage } from '../../interfaces/eb-product.interface'
import { EbProductAdapter } from '../connectors/eb-product.adapter'

@Injectable()
export class EbProductService extends ProductService {
  private currentProductCode = new BehaviorSubject(undefined)
  private metaProductData$ = new BehaviorSubject(undefined)
  metaProduct$ = this.metaProductData$.asObservable()

  constructor(
    store: Store<StateWithProduct>,
    productLoading: ProductLoadingService,
    private readonly productAdapter: EbProductAdapter
  ) {
    super(store, productLoading)
  }

  // overwritten to change return value to EbProduct
  get(
    productCode: string,
    scopes: (ProductScope | string)[] | ProductScope | string = DEFAULT_SCOPE
  ): Observable<EbProduct> {
    return productCode ? this.productLoading.get(productCode, [].concat(scopes)) : of(undefined)
  }

  getVariants(
    unitValue: string,
    productCode: string,
    pageSize = 10,
    page = 0,
    sorting = 'nameAsc',
    query = '',
    freeTextSearch = ''
  ): Observable<EbProductSearchPage> {
    return this.productAdapter.fetchVariants(
      productCode,
      pageSize,
      page,
      sorting,
      unitValue,
      query,
      freeTextSearch
    )
  }

  decryptVariantCode(encrypted): Observable<string> {
    return this.productAdapter.decryptVariantCode(encrypted)
  }

  downloadDocument(mediaCode: string) {
    return this.productAdapter
      .getDownloadDocument(mediaCode)
      .pipe(first())
      .subscribe(document => {
        saveAs(document, mediaCode, { autoBom: false })
      })
  }

  getProductMetaDataForSEO(productCode: string): Observable<EBStructuredProductData> {
    if (
      productCode !== this.currentProductCode.getValue() ||
      (this.currentProductCode.getValue() && !this.metaProductData$.getValue()?.sku)
    ) {
      this.currentProductCode.next(productCode)
      return this.productAdapter
        .fetchStructuredProductData(productCode)
        .pipe(tap(productData => this.metaProductData$.next(productData)))
    } else {
      return this.metaProduct$
    }
  }
}
